/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, ElementRef, HostListener, ViewChild, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LucideArrowUp, LucideUpload, LucideX } from '@lucide/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { UNSPLASH_IMAGES_DATA_SERVICE } from '../../../page-modules/admin/services/images/unsplash-images-data.service';
import { ImageCropResult, ImageCropperData } from '../../models/images/image-crop.model';
import { RestClientService } from '../../services/rest-client.service';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '../../services/toast-notifications/toast-service';
import { unsplashServiceFactory } from '../../shared-service-factory';
import { ButtonComponent } from '../../styleguide/components/buttons/default/button.component';
import { IconComponent } from '../../styleguide/components/icon/icon.component';
import { V3ImageCropperComponent } from '../image-cropper/v3-image-cropper.component';
import { V3UnsplashSearchComponent } from '../unsplash-search/v3-unsplash-search.component';
import { V3ImageUploadDialogData } from './v3-image-upload-dialog.model';

// The dialog opens on one of these, picked at random, so it is not the same photos every time.
const SUGGESTED_SEARCH_KEYS = [
  'translations.imageUploadV3.suggestion.education',
  'translations.imageUploadV3.suggestion.students',
  'translations.imageUploadV3.suggestion.learning',
  'translations.imageUploadV3.suggestion.classroom',
  'translations.imageUploadV3.suggestion.library',
  'translations.imageUploadV3.suggestion.books',
  'translations.imageUploadV3.suggestion.teamwork',
  'translations.imageUploadV3.suggestion.workplace',
  'translations.imageUploadV3.suggestion.technology',
  'translations.imageUploadV3.suggestion.natureLandscape',
];
// Matches what the V2 cover dialog accepted: the crop step rasterises SVG and passes animated GIFs
// through, so narrowing this to PNG/JPG/WebP would reject files that used to work.
const DEFAULT_ACCEPT = 'image/*';
const DEFAULT_MAX_FILE_SIZE_MB = 25;
const BYTES_IN_MB = 1024 * 1024;

/**
 * "Add an image" for V3 surfaces: pick a photo from Unsplash or upload a file, then crop it. A file can
 * come from the upload button, a drop anywhere on the dialog, or a paste. The V2
 * CoverImageUploadDialogComponent takes the same data, so a call site moves across by swapping the
 * component and the dialog config.
 *
 * The Unsplash data service is provided here rather than inherited: the dialog opens from modules that
 * do not import PtlEditorModule, which is the only other place that token is bound.
 */
@Component({
  selector: 'ptl-v3-image-upload-dialog',
  templateUrl: './v3-image-upload-dialog.component.html',
  styleUrls: ['./v3-image-upload-dialog.component.scss'],
  standalone: true,
  imports: [TranslocoModule, ButtonComponent, IconComponent, V3UnsplashSearchComponent, V3ImageCropperComponent],
  providers: [
    {
      provide: UNSPLASH_IMAGES_DATA_SERVICE,
      useFactory: unsplashServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
  ],
})
export class V3ImageUploadDialogComponent {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  readonly LucideX = LucideX;
  readonly LucideArrowUp = LucideArrowUp;
  readonly LucideUpload = LucideUpload;

  isDragging = signal(false);
  /** The image being cropped; null while the picker is on show. */
  cropData = signal<ImageCropperData | null>(null);

  readonly data = inject<V3ImageUploadDialogData>(MAT_DIALOG_DATA);

  private readonly dialogRef = inject<MatDialogRef<V3ImageUploadDialogComponent, ImageCropResult>>(MatDialogRef);
  private readonly transloco = inject(TranslocoService);
  private readonly toastService = inject<ToastService>(TOAST_NOTIFICATION_SERVICE);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly suggestedPhrases = SUGGESTED_SEARCH_KEYS.map((key) => this.translate(key));
  readonly initialSearchPhrase = this.data?.initialSearchPhrase ?? this.pickSuggestedPhrase();

  readonly title = computed(() => {
    if (this.cropData()) {
      return this.translate('translations.global.imageCrop.title');
    }
    return this.translate('translations.folio.label.addImage');
  });

  readonly cropDescription = this.translate('translations.imageUploadV3.description.crop');
  readonly backLabel = this.translate('translations.global.button.back');

  get isCropping(): boolean {
    return this.cropData() !== null;
  }

  get accept(): string {
    return this.data?.accept ?? DEFAULT_ACCEPT;
  }

  get maxFileSizeMb(): number {
    return this.data?.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB;
  }

  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (this.isCropping) {
      return;
    }

    // Pasting text into the search box has no files, so it is left alone.
    const pastedFile = event.clipboardData?.files?.item(0);
    if (!pastedFile) {
      return;
    }
    this.acceptFile(pastedFile);
  }

  /**
   * A file drag is always taken, even while cropping: left alone, the browser would open the dropped
   * file in the tab and the dialog would be lost.
   */
  onDragOver(event: DragEvent): void {
    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    if (!this.isCropping) {
      this.isDragging.set(true);
    }
  }

  /** dragleave also fires when moving between the dialog's own children, so only leaving the dialog counts. */
  onDragLeave(event: DragEvent): void {
    const enteredElement = event.relatedTarget as Node | null;
    if (enteredElement && this.hostElement.nativeElement.contains(enteredElement)) {
      return;
    }
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (!this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    this.isDragging.set(false);
    if (this.isCropping) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files) {
      this.onFilesDropped(files);
    }
  }

  openFilePicker(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files?.item(0);
    if (selectedFile) {
      this.acceptFile(selectedFile);
    }
    input.value = '';
  }

  onFilesDropped(files: FileList): void {
    if (files.length === 0) {
      return;
    }
    if (files.length > 1) {
      this.toastService.showFail(this.translate('translations.fileDropzone.errors.errorTooManyFiles'));
      return;
    }
    this.acceptFile(files.item(0));
  }

  onPhotoPicked(file: File): void {
    this.startCrop(file);
  }

  onCropApplied(result: ImageCropResult): void {
    this.dialogRef.close(result);
  }

  /** Back to the picker with the search results still on screen, rather than closing the dialog. */
  onCropCancelled(): void {
    this.cropData.set(null);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private acceptFile(file: File): void {
    if (file.size > this.maxFileSizeMb * BYTES_IN_MB) {
      this.toastService.showFail(this.translate('translations.fileDropzone.errors.errorFileSizeExceedsLimit'));
      return;
    }
    this.startCrop(file);
  }

  /** Cropping is the dialog's second step rather than a dialog of its own, so nothing stacks. */
  private startCrop(imageFile: File): void {
    this.cropData.set({
      imageFile,
      cropperShape: this.data?.cropperShape ?? 'rectangle',
      aspectRatio: this.data?.aspectRatio,
      resizeToWidth: this.data?.resizeToWidth,
      resizeToHeight: this.data?.resizeToHeight,
      containWithinAspectRatio: this.data?.containWithinAspectRatio,
      onlyScaleDown: this.data?.onlyScaleDown,
      imageFormat: this.data?.imageFormat,
      description: this.data?.description,
    });
  }

  /** Dragging one of the Unsplash thumbnails around must not light up the drop overlay. */
  private isFileDrag(event: DragEvent): boolean {
    const types = event.dataTransfer?.types ?? [];
    return Array.from(types).includes('Files');
  }

  private pickSuggestedPhrase(): string {
    const index = Math.floor(Math.random() * this.suggestedPhrases.length);
    return this.suggestedPhrases[index];
  }

  private translate(key: string): string {
    return this.transloco.translate(key);
  }
}
